import { InfoCircleOutlined } from '@ant-design/icons'
import { Modal, Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import { PrioritySaveBar } from '@/components/PrioritySortShared/PrioritySaveBar/PrioritySaveBar'
import { PriorityManager } from '@/components/StorePriority/ManualPriorityConfig/ManualPriorityConfig'
import {
  StorePriorityItem,
  StorePriorityRow,
} from '@/components/StorePriority/StorePriorityRow/StorePriorityRow'
import { MAX_PRIORITY } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { compareStores } from '@/utils/search/stores/sortStores'

import cn from './StorePreferences.module.scss'
import {
  GlobalPreferencePermissionsDto,
  StoreSettingsResponse,
  UnifiedStoreSettingUpdateDto,
} from './StorePreferences.types'

type ClientStorePriorityItem = StorePriorityItem & {
  isExcludedBySupplier: boolean
  isExcludedByClient: boolean
  isEffectivelyExcluded: boolean
}

type History = {
  initial: ClientStorePriorityItem[]
  present: ClientStorePriorityItem[]
  past: ClientStorePriorityItem[][]
  future: ClientStorePriorityItem[][]
}

const comparableState = (item: ClientStorePriorityItem) => ({
  priority: item.isMain ? item.priority : null,
  isExcludedBySupplier: item.isExcludedBySupplier,
  isExcludedByClient: item.isExcludedByClient,
})

const normalizeItems = (items: ClientStorePriorityItem[]) => [
  ...items
    .filter((item) => item.isMain)
    .sort((a, b) => a.priority - b.priority || compareStores(a, b)),
  ...items
    .filter((item) => !item.isMain)
    .sort((a, b) => a.systemPriority - b.systemPriority || compareStores(a, b)),
]

function StorePreferences() {
  const { code } = useParams()
  const { organisation, selectedOrganisation, currentSupplierId } =
    useUserInfoStore((state) => state)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [permissions, setPermissions] =
    useState<GlobalPreferencePermissionsDto | null>(null)
  const [history, setHistory] = useState<History | null>(null)
  const [modal, contextHolder] = Modal.useModal()

  const currentOrgId = useMemo(() => {
    if (
      Object.keys(selectedOrganisation).length &&
      String(selectedOrganisation.code) === code
    ) {
      return selectedOrganisation.id
    }
    return organisation.id
  }, [code, organisation.id, selectedOrganisation])

  const isOrgSelected = Object.keys(selectedOrganisation).length > 0

  const flatten = useCallback(
    (data: StoreSettingsResponse) =>
      normalizeItems(
        data.supplierGroups.flatMap((group) =>
          group.settings.map((setting) => ({
            id: setting.store.id,
            code: setting.store.code,
            name: setting.store.description,
            supplierName: group.supplier.description,
            isOwn: group.supplier.id === currentSupplierId,
            isActive: setting.store.isActive,
            isRemote: setting.store.isRemote,
            isMain: setting.priority != null,
            priority: setting.priority ?? MAX_PRIORITY,
            systemPriority: setting.systemPriority,
            isExcludedBySupplier: setting.isExcludedBySupplier,
            isExcludedByClient: setting.isExcludedByClient,
            isEffectivelyExcluded: setting.isEffectivelyExcluded,
          }))
        )
      ),
    [currentSupplierId]
  )

  const fetchData = useCallback(async () => {
    if (!currentOrgId) return
    setLoading(true)
    try {
      const response = await api.get<StoreSettingsResponse>(
        `/organisations/${currentOrgId}/store-preferences`
      )
      const items = flatten(response.data)
      setPermissions(response.data.permissions)
      setHistory({ initial: items, present: items, past: [], future: [] })
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }, [currentOrgId, flatten, modal])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const updateItems = useCallback((items: ClientStorePriorityItem[]) => {
    const normalized = normalizeItems(items)
    setHistory((previous) =>
      previous
        ? {
            ...previous,
            present: normalized,
            past: [...previous.past, previous.present],
            future: [],
          }
        : previous
    )
  }, [])

  const updateExclusion = useCallback(
    (
      id: string,
      field: 'isExcludedBySupplier' | 'isExcludedByClient',
      value: boolean
    ) => {
      if (!history) return
      updateItems(
        history.present.map((item) => {
          if (item.id !== id) return item
          const updated = { ...item, [field]: value }
          return {
            ...updated,
            isEffectivelyExcluded:
              updated.isExcludedBySupplier || updated.isExcludedByClient,
          }
        })
      )
    },
    [history, updateItems]
  )

  const undo = useCallback(() => {
    setHistory((previous) =>
      !previous?.past.length
        ? previous
        : {
            ...previous,
            present: previous.past[previous.past.length - 1],
            past: previous.past.slice(0, -1),
            future: [previous.present, ...previous.future],
          }
    )
  }, [])

  const redo = useCallback(() => {
    setHistory((previous) =>
      !previous?.future.length
        ? previous
        : {
            ...previous,
            present: previous.future[0],
            past: [...previous.past, previous.present],
            future: previous.future.slice(1),
          }
    )
  }, [])

  const changedIds = useMemo(() => {
    if (!history) return new Set<string>()
    const initial = new Map(
      history.initial.map((item) => [item.id, comparableState(item)])
    )
    return new Set(
      history.present
        .filter((item) => {
          const original = initial.get(item.id)
          const current = comparableState(item)
          return (
            !original ||
            Object.keys(current).some(
              (key) =>
                current[key as keyof typeof current] !==
                original[key as keyof typeof original]
            )
          )
        })
        .map((item) => item.id)
    )
  }, [history])

  const save = useCallback(async () => {
    if (!history || !currentOrgId || changedIds.size === 0) return
    const initial = new Map(
      history.initial.map((item) => [item.id, comparableState(item)])
    )
    const settings: UnifiedStoreSettingUpdateDto[] = history.present.reduce<
      UnifiedStoreSettingUpdateDto[]
    >((result, item) => {
      const original = initial.get(item.id)
      const current = comparableState(item)
      const changed =
        !original ||
        Object.keys(current).some(
          (key) =>
            current[key as keyof typeof current] !==
            original[key as keyof typeof original]
        )
      if (changed) {
        result.push({
          storeId: item.id,
          priority: current.priority,
          isExcludedBySupplier: item.isExcludedBySupplier,
          isExcludedByClient: item.isExcludedByClient,
        })
      }
      return result
    }, [])
    setSaving(true)
    const loadingToast = toast.loading('Сохранение настроек складов...')
    try {
      const response = await api.put<StoreSettingsResponse>(
        `/organisations/${currentOrgId}/store-preferences`,
        { settings }
      )
      const items = flatten(response.data)
      setPermissions(response.data.permissions)
      setHistory({ initial: items, present: items, past: [], future: [] })
      toast.success('Настройки складов сохранены', { id: loadingToast })
    } catch (error) {
      toast.dismiss(loadingToast)
      handleApiError(error, modal)
    } finally {
      setSaving(false)
    }
  }, [changedIds.size, currentOrgId, flatten, history, modal])

  return (
    <section className={cn.storePreferences}>
      {contextHolder}
      {loading && !history ? (
        <Loader size={20} />
      ) : (
        history && (
          <PriorityManager
            items={history.present}
            initialItems={history.initial}
            onDataChange={updateItems}
            onUndo={undo}
            onRedo={redo}
            canUndo={history.past.length > 0}
            canRedo={history.future.length > 0}
            canEditPriority={permissions?.canEditPriority ?? false}
            mainTitle={
              <>
                Приоритет переопределен{' '}
                <Tooltip title="Этот порядок применяется только к текущей организации.">
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            othersTitle={
              <>
                Используется системный приоритет{' '}
                <Tooltip title="Склад сохраняет системный порядок, пока его не перенесут в блок с переопределенным приоритетом.">
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            mainEmptyText="Перетащите сюда склад, чтобы переопределить приоритет"
            othersEmptyText="Все склады используют системный приоритет"
            renderRow={(props) => (
              <StorePriorityRow
                {...props}
                data={props.data}
                isChanged={changedIds.has(props.data.id)}
                dragProps={permissions?.canEditPriority ? props.dragProps : {}}
                canEditPriority={permissions?.canEditPriority ?? false}
                canEditSupplierExclusion={permissions?.canEditSupplierExclusion}
                canEditClientExclusion={permissions?.canEditClientExclusion}
                showSystemPriority
                onToggleSupplierExclusion={(id, value) =>
                  updateExclusion(id, 'isExcludedBySupplier', value)
                }
                onToggleClientExclusion={(id, value) =>
                  updateExclusion(id, 'isExcludedByClient', value)
                }
              />
            )}
          />
        )
      )}
      <PrioritySaveBar
        changeCount={changedIds.size}
        onSave={save}
        onUndo={undo}
        onRedo={redo}
        canUndo={Boolean(history?.past.length)}
        canRedo={Boolean(history?.future.length)}
        loading={saving}
        isBlueTheme={isOrgSelected}
      />
    </section>
  )
}

export default StorePreferences
